/**
 * Marketing / BD selectors — leads + tenders + opportunities + activities +
 * client enrichment.
 */

import type {
  Client,
  Lead,
  MarketingActivity,
  MockState,
  Opportunity,
  Tender,
} from '../types';

// ---- Weighted pipeline (FR-010, FR-017, FR-121) --------------------------

export function selectWeightedPipelineValue(state: MockState): number {
  return state.leads
    .filter((l: Lead) => l.stage !== 'won' && l.stage !== 'lost')
    .reduce((acc, l) => acc + l.value * l.probability, 0);
}

// ---- Pipeline funnel by lead stage (count + value) ----------------------

export interface LeadStageRow {
  stage: Lead['stage'];
  count: number;
  value: number;
}

export function selectLeadsByStage(state: MockState): LeadStageRow[] {
  const acc = new Map<Lead['stage'], LeadStageRow>();
  for (const l of state.leads) {
    const current = acc.get(l.stage) ?? { stage: l.stage, count: 0, value: 0 };
    current.count += 1;
    current.value += l.value;
    acc.set(l.stage, current);
  }
  // Preserve canonical order.
  const order: Lead['stage'][] = ['new', 'qualified', 'proposal', 'negotiation', 'won', 'lost'];
  return order
    .map((s) => acc.get(s))
    .filter((r): r is LeadStageRow => r !== undefined);
}

// ---- Tender value by stage ----------------------------------------------

export interface TenderStageRow {
  stage: Tender['stage'];
  count: number;
  value: number;
}

export function selectTendersByStage(state: MockState): TenderStageRow[] {
  const acc = new Map<Tender['stage'], TenderStageRow>();
  for (const t of state.tenders) {
    const current = acc.get(t.stage) ?? { stage: t.stage, count: 0, value: 0 };
    current.count += 1;
    current.value += t.value;
    acc.set(t.stage, current);
  }
  return Array.from(acc.values());
}

// ---- Hottest leads (weighted value desc) --------------------------------

export function selectHottestLeads(state: MockState, topN = 5): Lead[] {
  return state.leads
    .filter((l) => l.stage !== 'won' && l.stage !== 'lost')
    .map((l) => ({ ...l, _weighted: l.value * l.probability }))
    .sort(
      (a, b) =>
        (b as Lead & { _weighted: number })._weighted -
        (a as Lead & { _weighted: number })._weighted,
    )
    .slice(0, topN);
}

/** Win rate: won / (won + lost). */
export function selectWinRate(state: MockState): number {
  const won = state.leads.filter((l) => l.stage === 'won').length;
  const lost = state.leads.filter((l) => l.stage === 'lost').length;
  if (won + lost === 0) return 0;
  return won / (won + lost);
}

/** New leads within last N days. */
export function selectNewLeads(state: MockState, withinDays = 30): number {
  const today = new Date('2026-08-31');
  const cutoff = new Date(today);
  cutoff.setDate(cutoff.getDate() - withinDays);
  const cutoffIso = cutoff.toISOString().slice(0, 10);
  return state.leads.filter((l) => l.createdAt >= cutoffIso).length;
}

// ---- Tender pipeline preview (FR-122 / S3 upstream) ---------------------

import type { CurrencyCode, IsoDate } from '../types';

export interface TenderPipelineCard {
  id: string;
  reference: string;
  title: string;
  clientName: string;
  value: number;
  currency: CurrencyCode;
  stage: Tender['stage'];
  submissionDate: IsoDate;
}

/**
 * Lightweight enriched tender preview for the Tender Pipeline page (T29).
 * Card-grid consumer — upstream for the Sprint 3 Lead→Award wizard.
 */
export function selectTendersEnriched(state: MockState): TenderPipelineCard[] {
  return state.tenders.map((tn) => {
    const client = tn.clientId ? state.clients.find((c) => c.id === tn.clientId) : undefined;
    return {
      id: tn.id,
      reference: `TND-${tn.id.slice(-3).toUpperCase()}`,
      title: tn.title,
      clientName: client?.name ?? '—',
      value: tn.value,
      currency: tn.currency,
      stage: tn.stage,
      submissionDate: tn.submissionDate,
    };
  });
}

// ---- Clients enriched (S3 Marketing Client Master) ----------------------

export interface EnrichedClient extends Client {
  leadCount: number;
  openLeadValue: number;
  wonLeadValue: number;
  contractCount: number;
  totalContractValue: number;
  activeTenderCount: number;
  lastActivityDate?: IsoDate;
}

export function selectEnrichedClients(state: MockState): EnrichedClient[] {
  return state.clients.map((client) => {
    const clientLeads = state.leads.filter((l) => l.clientId === client.id);
    const openLeads = clientLeads.filter((l) => l.stage !== 'won' && l.stage !== 'lost');
    const wonLeads = clientLeads.filter((l) => l.stage === 'won');
    const clientContracts = state.contracts.filter((c) => c.clientId === client.id);
    const clientTenders = state.tenders.filter(
      (t) => t.clientId === client.id && t.stage !== 'awarded' && t.stage !== 'lost',
    );
    const clientActivities = state.marketingActivities
      .filter((a) => a.clientId === client.id)
      .sort((a, b) => b.date.localeCompare(a.date));

    return {
      ...client,
      leadCount: clientLeads.length,
      openLeadValue: openLeads.reduce((sum, l) => sum + l.value, 0),
      wonLeadValue: wonLeads.reduce((sum, l) => sum + l.value, 0),
      contractCount: clientContracts.length,
      totalContractValue: clientContracts.reduce((sum, c) => sum + c.originalValue, 0),
      activeTenderCount: clientTenders.length,
      ...(clientActivities[0] ? { lastActivityDate: clientActivities[0].date } : {}),
    };
  });
}

// ---- Opportunity pipeline -----------------------------------------------

export interface OpportunityStageRow {
  stage: Opportunity['stage'];
  count: number;
  value: number;
  weightedValue: number;
}

export function selectOpportunitiesByStage(state: MockState): OpportunityStageRow[] {
  const order: Opportunity['stage'][] = [
    'discovery',
    'scoping',
    'proposalDraft',
    'negotiation',
    'won',
    'lost',
  ];
  return order.map((stage) => {
    const bucket = state.opportunities.filter((o) => o.stage === stage);
    return {
      stage,
      count: bucket.length,
      value: bucket.reduce((acc, o) => acc + o.estimatedValue, 0),
      weightedValue: bucket.reduce((acc, o) => acc + o.estimatedValue * o.probability, 0),
    };
  });
}

/** Weighted opportunity pipeline — sum of estimatedValue × probability for open stages. */
export function selectWeightedOpportunityValue(state: MockState): number {
  return state.opportunities
    .filter((o) => o.stage !== 'won' && o.stage !== 'lost')
    .reduce((acc, o) => acc + o.estimatedValue * o.probability, 0);
}

// ---- Activities ----------------------------------------------------------

export interface EnrichedActivity extends MarketingActivity {
  linkedEntityName: string;
  linkedEntityKind: 'lead' | 'client' | 'tender' | 'none';
}

export function selectEnrichedActivities(state: MockState): EnrichedActivity[] {
  return state.marketingActivities
    .map((act) => {
      if (act.leadId) {
        const lead = state.leads.find((l) => l.id === act.leadId);
        return {
          ...act,
          linkedEntityKind: 'lead' as const,
          linkedEntityName: lead?.title ?? '—',
        };
      }
      if (act.clientId) {
        const client = state.clients.find((c) => c.id === act.clientId);
        return {
          ...act,
          linkedEntityKind: 'client' as const,
          linkedEntityName: client?.name ?? '—',
        };
      }
      if (act.tenderId) {
        const tender = state.tenders.find((t) => t.id === act.tenderId);
        return {
          ...act,
          linkedEntityKind: 'tender' as const,
          linkedEntityName: tender?.title ?? '—',
        };
      }
      return {
        ...act,
        linkedEntityKind: 'none' as const,
        linkedEntityName: '—',
      };
    })
    .sort((a, b) => b.date.localeCompare(a.date));
}

// ---- Marketing reporting charts -----------------------------------------

export interface TenderWinRateRow {
  quarter: string;
  won: number;
  lost: number;
  inFlight: number;
  winRate: number;
}

/** Win-rate by quarter derived from tender submissionDate. */
export function selectTenderWinRateByQuarter(state: MockState): TenderWinRateRow[] {
  const byQuarter = new Map<string, TenderWinRateRow>();
  for (const t of state.tenders) {
    const month = Number(t.submissionDate.slice(5, 7));
    const year = t.submissionDate.slice(0, 4);
    const q = `${year} Q${Math.ceil(month / 3)}`;
    const current = byQuarter.get(q) ?? {
      quarter: q,
      won: 0,
      lost: 0,
      inFlight: 0,
      winRate: 0,
    };
    if (t.stage === 'awarded') current.won += 1;
    else if (t.stage === 'lost') current.lost += 1;
    else current.inFlight += 1;
    byQuarter.set(q, current);
  }
  return Array.from(byQuarter.values())
    .map((row) => {
      const closed = row.won + row.lost;
      return { ...row, winRate: closed === 0 ? 0 : row.won / closed };
    })
    .sort((a, b) => a.quarter.localeCompare(b.quarter));
}

export interface PipelineFunnelRow {
  stage: Lead['stage'];
  count: number;
  value: number;
  conversionToWon: number;
}

/** Conversion-funnel rollup: % of each stage that eventually converts to won. */
export function selectPipelineConversionFunnel(state: MockState): PipelineFunnelRow[] {
  const base = selectLeadsByStage(state);
  const totalWon = state.leads.filter((l) => l.stage === 'won').length;
  return base.map((row) => ({
    ...row,
    conversionToWon: row.count === 0 ? 0 : Math.min(1, totalWon / Math.max(row.count, 1)),
  }));
}
